import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

// Q-Day target: Jan 1, 2030 UTC. Google now targets 2029 for a CRQC;
// expert consensus is the 2030s. We use 2030-01-01 as the round
// reference point.
const Q_DAY_TARGET = new Date('2030-01-01T00:00:00Z').getTime()

interface Remaining {
  years: number
  days: number
  hours: number
  minutes: number
  seconds: number
  expired: boolean
}

function computeRemaining(now: number): Remaining {
  const ms = Q_DAY_TARGET - now
  if (ms <= 0) {
    return { years: 0, days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
  }
  const YEAR_MS = 365.25 * 86400 * 1000
  const years = Math.floor(ms / YEAR_MS)
  let rem = ms - years * YEAR_MS
  const days = Math.floor(rem / (86400 * 1000))
  rem -= days * 86400 * 1000
  const hours = Math.floor(rem / (3600 * 1000))
  rem -= hours * 3600 * 1000
  const minutes = Math.floor(rem / (60 * 1000))
  rem -= minutes * 60 * 1000
  const seconds = Math.floor(rem / 1000)
  return { years, days, hours, minutes, seconds, expired: false }
}

function pad(n: number, w = 2) { return String(n).padStart(w, '0') }

interface QDayCountdownProps {
  variant?: 'full' | 'compact'
  showCTA?: boolean
}

function QDayCountdown({ variant = 'full', showCTA = true }: QDayCountdownProps) {
  const [remaining, setRemaining] = useState<Remaining>(() => computeRemaining(Date.now()))

  useEffect(() => {
    const id = setInterval(() => setRemaining(computeRemaining(Date.now())), 1000)
    return () => clearInterval(id)
  }, [])

  const compact = variant === 'compact'

  return (
    <div className={`relative overflow-hidden rounded-2xl shadow-xl ${compact ? '' : 'shadow-2xl'}`}>
      {/* Dark gradient backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-rose-950" />

      {/* Subtle grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.08]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="qday-grid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#qday-grid)" />
      </svg>

      {/* Glowing edge */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-rose-400/60 to-transparent" />
      <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-rose-500/40 to-transparent" />

      <div className={`relative ${compact ? 'px-4 py-3.5' : 'px-6 py-6 md:px-10 md:py-7'}`}>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Left: label + tagline */}
          <div className={`flex-1 min-w-0 ${compact ? '' : 'md:flex-[0_0_auto]'}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="relative flex w-2 h-2">
                <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-rose-400 opacity-75" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-rose-500" />
              </span>
              <span className="text-[10px] md:text-[11px] uppercase tracking-[0.3em] font-bold text-rose-300">
                Q-Day Countdown · est. 2030
              </span>
            </div>
            <div className={`font-bold text-white ${compact ? 'text-[13px] leading-tight' : 'text-base md:text-lg leading-tight'}`}>
              Pre-quantum cybersecurity is under attack.
            </div>
            {!compact && (
              <div className="text-[12px] md:text-[13px] text-slate-300 mt-1 max-w-md">
                Machine-checked Lean proofs of every resource bound that determines{' '}
                <span className="text-rose-300 font-semibold">when RSA-2048 falls</span>.
              </div>
            )}
          </div>

          {/* Right: ticking digits */}
          <div className="flex items-stretch gap-1.5 md:gap-2">
            <TimeBlock value={remaining.years} label="years" compact={compact} />
            <TimeBlock value={remaining.days} label="days" compact={compact} width={3} />
            <TimeBlock value={remaining.hours} label="hrs" compact={compact} />
            <TimeBlock value={remaining.minutes} label="min" compact={compact} />
            <TimeBlock value={remaining.seconds} label="sec" compact={compact} pulse />
          </div>

          {/* CTA (only when there's room) */}
          {showCTA && !compact && (
            <Link
              to="/resource-verification"
              className="hidden md:inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/20 hover:bg-white/20 hover:border-rose-300/60 transition-colors"
            >
              See verified bounds →
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

function TimeBlock({ value, label, compact, width = 2, pulse = false }: {
  value: number, label: string, compact: boolean, width?: number, pulse?: boolean
}) {
  return (
    <div className={`flex flex-col items-center justify-center ${compact ? 'min-w-[44px] px-1.5 py-1' : 'min-w-[56px] md:min-w-[68px] px-2 py-1.5'} rounded-md bg-white/5 border border-white/10`}>
      <div className={`font-mono font-bold text-white tabular-nums ${compact ? 'text-[15px]' : 'text-xl md:text-2xl'} ${pulse ? 'tracking-wider' : ''}`}>
        {pad(value, width)}
      </div>
      <div className={`uppercase tracking-wider text-rose-200/80 font-semibold ${compact ? 'text-[8px]' : 'text-[9px] md:text-[10px]'}`}>
        {label}
      </div>
    </div>
  )
}

export default QDayCountdown
